import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "professional_certificate_development";

const ProfessionalCertificateDevelopment = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    year: null,
    certificate_name: "",
    certificate_level: "",
    role: "",
    nature: "",
    institution: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    // Sort latest year/month to oldest
    const sortedData = Array.isArray(data)
      ? data.sort((a, b) => new Date(b.year) - new Date(a.year))
      : [];

    setRecords(sortedData);

  } catch (err) {
    toast.error(`Fetch error: ${err.message}`);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        year: formData.year ? format(formData.year, "yyyy-MM") + "-01" : null,
      };

      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Record saved successfully!");
      setFormData({
        year: null,
        certificate_name: "",
        certificate_level: "",
        role: "",
        nature: "",
        institution: "",
        country: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (record) => {
    setFormData({
      year: record.year ? new Date(record.year) : null,
      certificate_name: record.certificate_name || "",
      certificate_level: record.certificate_level || "",
      role: record.role || "",
      nature: record.nature || "",
      institution: "",
      country: "",
    });
    setRecordId(record.id);
    toast.info("Editing record...");
  };

  const handleDelete = (record) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${record.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const datePickerStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Professional Certificate Course Developments
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Year Picker */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Certificate Year (mm/yyyy)
              <span
                style={{
                  fontWeight: 300,
                  fontSize: "0.85em",
                  marginLeft: "6px",
                  color: "#666",
                }}
              >
                (start with latest certificate)
              </span>
            </label>
            <DatePicker
              selected={formData.year}
              onChange={(date) => setFormData({ ...formData, year: date })}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              placeholderText="Select month/year"
              style={datePickerStyle}
            />
          </div>

          {/* Certificate Name */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Certificate Name
            </label>
            <input
              type="text"
              name="certificate_name"
              value={formData.certificate_name}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          {/* Certificate Level Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Certificate Level
            </label>
            <select
              name="certificate_level"
              value={formData.certificate_level}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
                fontSize: "16px",
              }}
              required
            >
              <option value="">Select</option>
              <option value="Undergraduate">Undergraduate</option>
              <option value="Post-Graduate">Post-Graduate</option>
            </select>
          </div>

          {/* Role Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
                fontSize: "16px",
              }}
              required
            >
              <option value="">Select</option>
              <option value="Lead">Lead</option>
              <option value="Member">Member</option>
            </select>
          </div>

          {/* Nature Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Nature
            </label>
            <select
              name="nature"
              value={formData.nature}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
                fontSize: "16px",
              }}
              required
            >
              <option value="">Select</option>
              <option value="Newly Developed">Newly Developed</option>
              <option value="Revision">Revision</option>
            </select>
          </div>

          {/* Institution */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Institution
            </label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          {/* Countru */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Year",
                    "Certificate Name",
                    "Certificate Level",
                    "Role",
                    "Nature",
                    "Institution",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px', background: '#f1f5f9'",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.year
                        ? `${rec.year.slice(5, 7)}/${rec.year.slice(0, 4)}`
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.certificate_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.certificate_level}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.role}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.nature}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalCertificateDevelopment;
